import { useState, useMemo, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useInvalidateAnalytics } from '@/hooks/useAnalytics';
import { GlobalFilterBar, type DatePreset, type Region } from '@/components/analytics/GlobalFilterBar';
import { AdOpsView } from '@/components/analytics/AdOpsView';
import { SalesView } from '@/components/analytics/SalesView';
import type { AnalyticsFilters } from '@/types';
import type { UserRole } from '@/types';

/** Roles that can see the AdOps tab */
const ADOPS_VIEW_ROLES: UserRole[] = ['adops_leadership', 'charting_manager', 'admin'];

/** Roles that can see the Sales tab */
const SALES_VIEW_ROLES: UserRole[] = [
  'adops_leadership',
  'charting_manager',
  'admin',
  'sales_vp',
  'gvp',
  'rvp',
];

type AnalyticsTab = 'adops' | 'sales';

/**
 * Analytics Dashboard page.
 * Provides global filters, tab navigation (AdOps / Sales), and role-based access control.
 */
export default function Analytics() {
  const { user, isLoading } = useAuthContext();
  const invalidateAnalytics = useInvalidateAnalytics();

  // Determine which views the user can access
  const userRole = user?.role as UserRole | undefined;
  const canViewAdOps = userRole ? ADOPS_VIEW_ROLES.includes(userRole) : false;
  const canViewSales = userRole ? SALES_VIEW_ROLES.includes(userRole) : false;
  const showTabs = canViewAdOps && canViewSales;

  // Default tab: AdOps if user has access, otherwise Sales
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('sales');

  // Correct the default tab once auth finishes loading
  useEffect(() => {
    if (!isLoading && canViewAdOps) {
      setActiveTab('adops');
    }
  }, [isLoading, canViewAdOps]);

  // Filter state — persists across tab switches
  const today = new Date();
  const [datePreset, setDatePreset] = useState<DatePreset>('last30');
  const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
  const [filters, setFilters] = useState<AnalyticsFilters>({
    startDate: format(subDays(today, 29), 'yyyy-MM-dd'),
    endDate: format(today, 'yyyy-MM-dd'),
    region: undefined,
  });

  // Refresh state for button animation
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    invalidateAnalytics();
    // Reset animation after a short delay
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Memoize the filters object to avoid unnecessary re-renders
  const stableFilters = useMemo(() => filters, [filters.startDate, filters.endDate, filters.region?.join(',')]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6B4FBB]" />
      </div>
    );
  }

  // Route-level auth guard: only SALES_VIEW_ROLES can access the page
  if (!canViewSales) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-[1280px] mx-auto px-6 py-6">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-[22px] font-bold text-[#111111] uppercase tracking-[-0.025em]">
            Analytics
          </h1>
          <button
            onClick={handleRefresh}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded border border-neutral-200',
              'bg-white text-[#111111] hover:bg-neutral-100 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB]'
            )}
            aria-label="Refresh all analytics data"
          >
            <RefreshCw
              className={cn('h-4 w-4', isRefreshing && 'animate-spin')}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>

        {/* Global filter bar */}
        <GlobalFilterBar
          filters={filters}
          onFiltersChange={setFilters}
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          selectedRegions={selectedRegions}
          onRegionsChange={setSelectedRegions}
        />

        {/* Tab navigation — only shown when user has access to both views */}
        {showTabs && (
          <div className="mt-6 border-b border-neutral-200" role="tablist" aria-label="Analytics views">
            <div className="flex gap-6">
              <button
                role="tab"
                aria-selected={activeTab === 'adops'}
                aria-controls="panel-adops"
                id="tab-adops"
                onClick={() => setActiveTab('adops')}
                className={cn(
                  'pb-3 text-sm font-semibold transition-colors border-b-2 -mb-px',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB] focus-visible:ring-offset-2',
                  activeTab === 'adops'
                    ? 'border-[#6B4FBB] text-[#111111]'
                    : 'border-transparent text-[#555555] hover:text-[#111111] hover:border-neutral-400'
                )}
              >
                AdOps
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'sales'}
                aria-controls="panel-sales"
                id="tab-sales"
                onClick={() => setActiveTab('sales')}
                className={cn(
                  'pb-3 text-sm font-semibold transition-colors border-b-2 -mb-px',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB] focus-visible:ring-offset-2',
                  activeTab === 'sales'
                    ? 'border-[#6B4FBB] text-[#111111]'
                    : 'border-transparent text-[#555555] hover:text-[#111111] hover:border-neutral-400'
                )}
              >
                Sales
              </button>
            </div>
          </div>
        )}

        {/* View content */}
        <div className="mt-6">
          {showTabs ? (
            <>
              <div
                role="tabpanel"
                id="panel-adops"
                aria-labelledby="tab-adops"
                hidden={activeTab !== 'adops'}
              >
                {activeTab === 'adops' && <AdOpsView filters={stableFilters} />}
              </div>
              <div
                role="tabpanel"
                id="panel-sales"
                aria-labelledby="tab-sales"
                hidden={activeTab !== 'sales'}
              >
                {activeTab === 'sales' && <SalesView filters={stableFilters} />}
              </div>
            </>
          ) : canViewAdOps ? (
            <AdOpsView filters={stableFilters} />
          ) : (
            <SalesView filters={stableFilters} />
          )}
        </div>
      </div>
    </div>
  );
}
